/* eslint-disable react-refresh/only-export-components */
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { HelpSheet } from '@/components/shared/HelpSheet';
import { PageContainer } from '@/components/shared/PageContainer';
import PageTabs from '@/components/shared/PageTabs';
import { useTranslator } from '@/hooks/useTranslator';
import { RiskRulesTable } from '../../../-components/RiskRulesTable';
import { useRiskRulesPage } from '../../../-hooks/useRiskRulesPage';
import type { RuleView } from '../../../-types';

const searchSchema = z.object({
  tab: z.string().optional(),
  page: z.number().optional(),
  limit: z.number().optional(),
  sort: z.string().optional(),
  filters: z.record(z.array(z.string())).optional(),
  search: z.string().optional(),
});

export const Route = createFileRoute('/(client)/risk/(pages)/(list)/rules/')({
  validateSearch: searchSchema,
  component: RiskRulesPage,
});

function RiskRulesPage() {
  const { getTranslation: t } = useTranslator({ page: 'riskRules' });
  const { tab } = Route.useSearch();

  const view: RuleView = tab === 'retraced' ? 'RETRACED' : 'CUSTOM';

  const {
    myCompanyId,
    helpOpen,
    setHelpOpen,
    selectedIds,
    setSelectedIds,
    list,
    createMutation,
    cloneMutation,
    canClone,
    handleView,
    handleEdit,
    handleBulkClone,
  } = useRiskRulesPage(view);

  return (
    <div className='flex flex-col h-full overflow-hidden'>
      <PageTabs
        tabs={[
          { key: 'custom', label: t('list.tabs.custom') },
          { key: 'retraced', label: t('list.tabs.retraced') },
        ]}
        defaultTab='custom'
        param='tab'
        moreLabel={t('list.tabs.more')}
      />

      <PageContainer>
        <RiskRulesTable
          rows={list.rows}
          total={list.total}
          isLoading={list.isLoading}
          myCompanyId={myCompanyId}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          canClone={canClone}
          sorting={list.sorting}
          filters={list.filters}
          pagination={list.pagination}
          onSortingChange={list.onSortingChange}
          onFiltersChange={list.onFiltersChange}
          onPaginationChange={list.onPaginationChange}
          onView={handleView}
          onEdit={handleEdit}
          onBulkClone={handleBulkClone}
          isBulkClonePending={cloneMutation.isPending}
          onSubmitCreate={title => createMutation.mutate(title)}
          isCreatePending={createMutation.isPending}
          onOpenHelp={() => setHelpOpen(true)}
        />
      </PageContainer>
      <HelpSheet
        articleId='8960666'
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
    </div>
  );
}
